import { Orcamento } from '@/contracts/schemas';
import { formatCurrency, formatDateBR } from './orcamento-calc';

function linha(rotulo: string, valor: string): string {
  return `${rotulo} *${valor}*`;
}

/**
 * Texto pronto para o WhatsApp com formatação elegante.
 * O negrito usa o asterisco que o aplicativo reconhece.
 */
export function buildWhatsAppQuoteMessage(orc: Orcamento, publicUrl: string): string {
  const prestador = orc.perfilMei.nomeFantasia || orc.perfilMei.nome;
  const itens = orc.itens.slice(0, 6).map((item) => {
    return `• ${item.descricao}\n   ${item.quantidade} ${item.unidade} · *${formatCurrency(item.subtotal)}*`;
  });
  const extras = orc.itens.length > 6 ? [`• e mais ${orc.itens.length - 6} item(ns) no link`] : [];
  const blocos = [
    `🧾 *Orçamento*`,
    linha('📌 Serviço:', orc.titulo),
    linha('🔢 Número:', orc.numero),
    ``,
    `Olá, *${orc.cliente.nome}*.`,
    `Segue a proposta de *${prestador}*.`,
    ``,
    `🛠️ *Itens*`,
    ...itens,
    ...extras,
    ``,
    linha('💰 Total:', formatCurrency(orc.valorTotal)),
  ];

  if (orc.valorSinal > 0) {
    blocos.push(linha(`⚡ Sinal Pix (${orc.percentualSinal}%):`, formatCurrency(orc.valorSinal)));
  }
  if (orc.valorRestante > 0) {
    blocos.push(linha('✅ Saldo na conclusão:', formatCurrency(orc.valorRestante)));
  }
  if (orc.prazoEstimado) blocos.push(linha('⏱️ Prazo:', orc.prazoEstimado));
  if (orc.dataValidade) blocos.push(linha('📅 Válido até:', formatDateBR(orc.dataValidade)));
  if (orc.condicoesPagamento) blocos.push(linha('💳 Condição:', orc.condicoesPagamento));

  blocos.push(
    ``,
    `🔗 *Abra a proposta e copie o código Pix:*`,
    publicUrl,
    ``,
    `O valor cai na chave Pix de *${prestador}*.`,
    `O ZapOrca não confirma o pagamento do banco.`
  );

  if (orc.observacoes) {
    blocos.push(``, linha('📝 Observação:', orc.observacoes.slice(0, 180)));
  }

  return blocos.join('\n');
}

/**
 * Mensagem em modo 100% compatível (sem emojis de 4 bytes que o Windows WhatsApp Desktop converte em '?')
 */
export function buildCleanWhatsAppQuoteMessage(orc: Orcamento, publicUrl: string): string {
  const prestador = orc.perfilMei.nomeFantasia || orc.perfilMei.nome;
  const itens = orc.itens.slice(0, 6).map((item) => {
    return `• ${item.descricao}\n   ${item.quantidade} ${item.unidade} · *${formatCurrency(item.subtotal)}*`;
  });
  const extras = orc.itens.length > 6 ? [`• e mais ${orc.itens.length - 6} item(ns) na proposta`] : [];
  const blocos = [
    `*PROPOSTA COMERCIAL*`,
    `• *Serviço:* ${orc.titulo}`,
    `• *Número:* ${orc.numero}`,
    ``,
    `Olá, *${orc.cliente.nome}*!`,
    `Segue a proposta de *${prestador}*.`,
    ``,
    `*ITENS:*`,
    ...itens,
    ...extras,
    ``,
    `*TOTAL:* *${formatCurrency(orc.valorTotal)}*`,
  ];

  if (orc.valorSinal > 0) {
    blocos.push(`• *Sinal Pix (${orc.percentualSinal}%):* *${formatCurrency(orc.valorSinal)}*`);
  }
  if (orc.valorRestante > 0) {
    blocos.push(`• *Saldo na entrega:* *${formatCurrency(orc.valorRestante)}*`);
  }
  if (orc.prazoEstimado) blocos.push(`• *Prazo:* ${orc.prazoEstimado}`);
  if (orc.dataValidade) blocos.push(`• *Válido até:* ${formatDateBR(orc.dataValidade)}`);
  if (orc.condicoesPagamento) blocos.push(`• *Condição:* ${orc.condicoesPagamento}`);

  blocos.push(
    ``,
    `*ACESSE A PROPOSTA COMPLETA & CHAVE PIX:*`,
    publicUrl,
    ``,
    `O valor é pago diretamente na chave Pix de *${prestador}*.`,
    `O ZapOrca não confirma o pagamento do banco.`
  );

  if (orc.observacoes) {
    blocos.push(``, `• *Observações:* ${orc.observacoes.slice(0, 180)}`);
  }

  return blocos.join('\n');
}
